import {isNil} from 'ramda';

import {InspectionStateLiteral, InspectionTypeLiteral} from '@dms/api/core';
import {usePermissions} from '@dms/shared';

import {Nullish, TestIdProps} from 'shared/web';

import {isCarvagoWorkspace} from '../../utils/isCarvagoWorkspace';
import {EquipmentDocumentCardContent} from './components/EquipmentDocumentCardContent';

export interface InspectionEquipmentDocumentCardProps extends TestIdProps {
  /** The inspection (audit) id; the card renders nothing without one. */
  auditId: string | Nullish;
  inspectionType: InspectionTypeLiteral | Nullish;
  inspectionState: InspectionStateLiteral | Nullish;
}

/**
 * One optional equipment PDF attached to an inspection (Carvago workspace only).
 *
 * Visibility follows the inspection READ permission; the upload, replace and remove controls
 * follow the UPDATE permission evaluated for the inspection's current state — deliberately not the
 * legacy "finished inspection is read-only" flag, because the backend allows managing the
 * attachment on finished inspections without a reopen transition.
 */
export function InspectionEquipmentDocumentCard(props: InspectionEquipmentDocumentCardProps) {
  const [canReadInspection, canUpdateInspection] = usePermissions({
    permissionKeys: ['readInspection', 'updateInspection'],
    scopes: {
      readInspection: {
        inspectionType: props.inspectionType,
        // Gated by inspection type, not participation
        participation: 'SKIP',
      },
      updateInspection: {
        inspectionType: props.inspectionType,
        inspectionState: props.inspectionState,
        // Gated by inspection type/state, not participation
        participation: 'SKIP',
      },
    },
  });

  if (isNil(props.auditId) || !isCarvagoWorkspace() || !canReadInspection) {
    return null;
  }

  return (
    <EquipmentDocumentCardContent
      auditId={props.auditId}
      canUpdate={canUpdateInspection}
      data-testid={props['data-testid']}
    />
  );
}
